import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { subjectList } from "../../../api/Subjects";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import { courseUpdate, courseDetail } from "../../../api/Courses";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import AcademicPortalLayout from "../AcademicPortalLayout";

export default function CourseUpdate() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [data, setData] = useState({});
  const [subjectSearch, setSubjectSearch] = useState("");

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const getSubjectList = async () => {
    setLoading(true);

    try {
      const response = await subjectList();

      if (response?.data?.status) {
        setSubjects(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load subjects.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const courseDetails = async () => {
    if (!id) {
      SweetAlertShow("info", "Warning", "Course ID is missing.");
      return;
    }

    setLoading(true);

    try {
      const response = await courseDetail(id);

      if (response?.data?.status) {
        const courseData = response?.data?.data || {};

        let parsedSubjects = [];

        try {
          const subjectValue = courseData?.subjects;

          if (Array.isArray(subjectValue)) {
            parsedSubjects = subjectValue.map(String);
          } else if (subjectValue) {
            const parsed =
              typeof subjectValue === "string"
                ? JSON.parse(subjectValue)
                : subjectValue;

            parsedSubjects = Array.isArray(parsed) ? parsed.map(String) : [];
          }
        } catch (error) {
          parsedSubjects = [];
        }

        setData({
          ...courseData,
          subjects: JSON.stringify(parsedSubjects),
        });
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load course details.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getSelectedSubjects = () => {
    try {
      if (Array.isArray(data?.subjects)) {
        return data.subjects.map(String);
      }

      if (!data?.subjects) {
        return [];
      }

      const parsed =
        typeof data.subjects === "string"
          ? JSON.parse(data.subjects)
          : data.subjects;

      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (error) {
      return [];
    }
  };

  const selectedSubjects = getSelectedSubjects();

  const filteredSubjects = useMemo(() => {
    const search = subjectSearch.trim().toLowerCase();

    if (!search) {
      return subjects;
    }

    return subjects.filter((subject) =>
      String(subject?.name || "")
        .toLowerCase()
        .includes(search),
    );
  }, [subjects, subjectSearch]);

  const updateSelectedSubjects = (updated) => {
    setData((previous) => ({
      ...previous,
      subjects: JSON.stringify(updated.map(String)),
    }));
  };

  const toggleSubject = (subjectId) => {
    const idString = String(subjectId);
    const current = getSelectedSubjects();

    if (current.includes(idString)) {
      updateSelectedSubjects(current.filter((item) => item !== idString));
    } else {
      updateSelectedSubjects([...current, idString]);
    }
  };

  const selectAllSubjects = () => {
    updateSelectedSubjects(subjects.map((subject) => String(subject.id)));
  };

  const clearAllSubjects = () => {
    updateSelectedSubjects([]);
  };

  const removeSubject = (subjectId) => {
    const updated = getSelectedSubjects().filter(
      (id) => id !== String(subjectId),
    );

    updateSelectedSubjects(updated);
  };

  const updateCourse = async (e) => {
    e.preventDefault();

    const selected = getSelectedSubjects();

    if (selected.length === 0) {
      SweetAlertShow(
        "info",
        "Select Subject",
        "Please select at least one subject for this course/class."
      );
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData(e.currentTarget);

      console.log("Submitted subjects:", formData.getAll("subjects[]"));

      const response = await courseUpdate(formData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message
        );
        navigate("/courses");
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubjectList();
    courseDetails();
  }, [id]);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <AcademicPortalLayout />

                  <div className="col-12">
                    {/* Page Header */}
                    <div className="card mb-4">
                      <div className="card-body py-4">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-3 bg-primary-subtle text-primary"
                              style={{
                                width: "52px",
                                height: "52px",
                                flexShrink: 0,
                              }}
                            >
                              <i className="ri-book-line fs-3"></i>
                            </div>

                            <div>
                              <h4 className="mb-1">Update Course / Class</h4>

                              <p className="text-muted mb-0">
                                Update course information and manage assigned
                                subjects.
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate("/courses")}
                          >
                            <i className="ri-arrow-left-line me-1"></i>
                            Back to Courses
                          </button>
                        </div>
                      </div>
                    </div>

                    <form onSubmit={updateCourse}>
                      <input
                        type="hidden"
                        name="id"
                        value={data?.id || id || ""}
                      />

                      {selectedSubjects.map((subjectId) => (
                        <input
                          key={subjectId}
                          type="hidden"
                          name="subjects[]"
                          value={subjectId}
                        />
                      ))}

                      {/* Course Information */}
                      <div className="card mb-4">
                        <div className="card-header border-bottom">
                          <div className="d-flex align-items-center gap-2">
                            <i className="ri-information-line text-primary fs-5"></i>

                            <div>
                              <h5 className="mb-0">Course Information</h5>

                              <small className="text-muted">
                                Basic details of the course or class.
                              </small>
                            </div>
                          </div>
                        </div>

                        <div className="card-body pt-4">
                          <div className="row g-4">
                            <div className="col-lg-6">
                              <label className="form-label fw-medium">
                                Course / Class Title
                                <span className="text-danger ms-1">*</span>
                              </label>

                              <div className="input-group">
                                <span className="input-group-text">
                                  <i className="ri-book-open-line"></i>
                                </span>

                                <input
                                  type="text"
                                  className="form-control input-capitalize"
                                  placeholder="Enter course or class title"
                                  name="title"
                                  required
                                  value={data?.title || ""}
                                  onChange={(e) =>
                                    setData({
                                      ...data,
                                      title: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>

                            <div className="col-lg-6">
                              <label className="form-label fw-medium">
                                Short Description
                              </label>

                              <div className="input-group">
                                <span className="input-group-text align-items-start pt-3">
                                  <i className="ri-file-text-line"></i>
                                </span>

                                <textarea
                                  className="form-control input-uppercase"
                                  placeholder="Enter a short description"
                                  name="description"
                                  rows="3"
                                  style={{ minHeight: "80px" }}
                                  value={data?.description || ""}
                                  onChange={(e) =>
                                    setData({
                                      ...data,
                                      description: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Subjects */}
                      <div className="card mb-4">
                        <div className="card-header border-bottom">
                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                            <div className="d-flex align-items-center gap-2">
                              <i className="ri-book-2-line text-primary fs-5"></i>

                              <div>
                                <h5 className="mb-0">Select Subjects</h5>

                                <small className="text-muted">
                                  Choose the subjects assigned to this
                                  course/class.
                                </small>
                              </div>
                            </div>

                            <div className="badge bg-primary-subtle text-primary px-3 py-2">
                              <i className="ri-checkbox-multiple-line me-1"></i>
                              {selectedSubjects.length} / {subjects.length}{" "}
                              Selected
                            </div>
                          </div>
                        </div>

                        <div className="card-body">
                          {/* Search / Actions */}
                          <div className="row g-3 align-items-center mb-4">
                            <div className="col-lg-7">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <i className="ri-search-line"></i>
                                </span>

                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search subjects..."
                                  value={subjectSearch}
                                  onChange={(e) =>
                                    setSubjectSearch(e.target.value)
                                  }
                                />

                                {subjectSearch && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setSubjectSearch("")}
                                    title="Clear search"
                                  >
                                    <i className="ri-close-line"></i>
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="col-lg-5">
                              <div className="d-flex justify-content-lg-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={selectAllSubjects}
                                  disabled={subjects.length === 0}
                                >
                                  <i className="ri-checkbox-multiple-line me-1"></i>
                                  Select All
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={clearAllSubjects}
                                  disabled={selectedSubjects.length === 0}
                                >
                                  <i className="ri-close-circle-line me-1"></i>
                                  Clear All
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Selected Subjects */}
                          {selectedSubjects.length > 0 && (
                            <div className="mb-4">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="fw-medium">
                                  Selected Subjects
                                </span>

                                <small className="text-muted">
                                  Click × to remove
                                </small>
                              </div>

                              <div className="d-flex flex-wrap gap-2">
                                {selectedSubjects.map((selectedId) => {
                                  const subject = subjects.find(
                                    (item) =>
                                      String(item.id) === String(selectedId),
                                  );

                                  if (!subject) {
                                    return null;
                                  }

                                  return (
                                    <span
                                      key={selectedId}
                                      className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 d-inline-flex align-items-center gap-2"
                                    >
                                      <i className="ri-book-line"></i>

                                      <span>{subject.name}</span>

                                      <button
                                        type="button"
                                        className="btn btn-sm p-0 text-primary"
                                        onClick={() =>
                                          removeSubject(subject.id)
                                        }
                                        style={{
                                          lineHeight: 1,
                                          border: 0,
                                          background: "transparent",
                                        }}
                                        title={`Remove ${subject.name}`}
                                      >
                                        <i className="ri-close-line"></i>
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Subject List */}
                          {filteredSubjects.length > 0 ? (
                            <div className="row g-3">
                              {filteredSubjects.map((subject) => {
                                const isSelected = selectedSubjects.includes(
                                  String(subject.id),
                                );

                                return (
                                  <div
                                    className="col-12 col-sm-6 col-lg-4 col-xl-3"
                                    key={subject.id}
                                  >
                                    <button
                                      type="button"
                                      className={`w-100 text-start p-3 rounded-3 border bg-body ${isSelected ? "border-primary" : ""
                                        }`}
                                      onClick={() => toggleSubject(subject.id)}
                                      style={{
                                        minHeight: "82px",
                                        transition: "all 0.2s ease",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-3">
                                        <div
                                          className={`d-flex align-items-center justify-content-center rounded-2 ${isSelected
                                            ? "bg-primary text-white"
                                            : "bg-body-secondary text-primary"
                                            }`}
                                          style={{
                                            width: "38px",
                                            height: "38px",
                                            flexShrink: 0,
                                          }}
                                        >
                                          <i
                                            className={
                                              isSelected
                                                ? "ri-check-line"
                                                : "ri-book-line"
                                            }
                                          ></i>
                                        </div>

                                        <div className="flex-grow-1 min-w-0">
                                          <div className="fw-medium text-truncate">
                                            {subject.name}
                                          </div>

                                          <small className="text-muted">
                                            {isSelected
                                              ? "Selected"
                                              : "Click to select"}
                                          </small>
                                        </div>

                                        <div
                                          className={`form-check mb-0 ${isSelected ? "text-primary" : ""
                                            }`}
                                        >
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            tabIndex={-1}
                                          />
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-5">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary text-muted mx-auto mb-3"
                                style={{
                                  width: "64px",
                                  height: "64px",
                                }}
                              >
                                <i className="ri-search-line fs-3"></i>
                              </div>

                              <h6 className="mb-1">No subjects found</h6>

                              <p className="text-muted mb-0">
                                {subjectSearch
                                  ? "Try a different subject search."
                                  : "No subjects are available."}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                            <div className="text-muted small">
                              <i className="ri-information-line me-1"></i>
                              At least one subject must be selected.
                            </div>

                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-secondary px-4"
                                onClick={() => navigate("/courses")}
                                disabled={loading}
                              >
                                <i className="ri-close-line me-1"></i>
                                Cancel
                              </button>

                              <button
                                type="submit"
                                className="btn btn-primary px-4"
                                disabled={
                                  loading || selectedSubjects.length === 0
                                }
                              >
                                {loading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-save-line me-1"></i>
                                    Update Course
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </form>

                    <style>{`
                      .course-update-subject-card {
                        transition: all 0.2s ease;
                      }

                      .course-update-subject-card:hover {
                        transform: translateY(-1px);
                      }

                      .min-w-0 {
                        min-width: 0;
                      }

                      @media (max-width: 575.98px) {
                        .course-update-page-actions {
                          width: 100%;
                        }

                        .course-update-page-actions .btn {
                          flex: 1;
                        }
                      }
                    `}</style>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
